import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Upload } from 'lucide-react';
import { ImportRow } from '@/lib/product-schema';

interface Props {
  open: boolean;
  rows: ImportRow[] | null;
  importing: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export default function ProductImportDialog({ open, rows, importing, onClose, onConfirm }: Props) {
  const { t } = useTranslation();
  const validCount = rows?.filter(r => r._valid).length ?? 0;
  const errorCount = (rows?.length ?? 0) - validCount;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader><DialogTitle>{t('cat_products.importPreview')}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          {rows && (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                {t('cat_products.importSummary', { total: rows.length, valid: validCount, errors: errorCount })}
              </p>
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">{t('cat_common.name')}</th>
                    <th className="p-2 text-left">SKU</th>
                    <th className="p-2 text-left">{t('cat_common.brand')}</th>
                    <th className="p-2 text-left">{t('cat_common.attribute')}</th>
                    <th className="p-2 text-left">MXIK</th>
                    <th className="p-2 text-left">{t('cat_products.mxikName')}</th>
                    <th className="p-2 text-left">{t('cat_products.barcodeShort')}</th>
                    <th className="p-2 text-left">{t('cat_common.category')}</th>
                    <th className="p-2 text-left">{t('cat_common.unit')}</th>
                    <th className="p-2 text-right">{t('cat_products.price')}</th>
                    <th className="p-2 text-left">{t('cat_products.tenors')}</th>
                    <th className="p-2 text-left">{t('cat_common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 50).map((row, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="p-2 text-muted-foreground">{row._row}</td>
                      <td className="p-2 max-w-[160px] truncate" title={row.name}>{row.name || '—'}</td>
                      <td className="p-2 font-mono">{row.sku || '—'}</td>
                      <td className="p-2">{row.brend_nomi || '—'}</td>
                      <td className="p-2 max-w-[120px] truncate" title={row.atribut_nomi}>{row.atribut_nomi || '—'}</td>
                      <td className="p-2 font-mono">{row.mhik || '—'}</td>
                      <td className="p-2 max-w-[140px] truncate" title={row.mxik_nomi}>{row.mxik_nomi || '—'}</td>
                      <td className="p-2 font-mono">{row.barcode || '—'}</td>
                      <td className="p-2">{row.category || '—'}</td>
                      <td className="p-2">{row.unit}</td>
                      <td className="p-2 text-right font-mono">{row.sale_price}</td>
                      <td className="p-2">{row.allowed_tenors?.join(', ') || '—'}</td>
                      <td className="p-2">
                        {row._valid
                          ? <Badge className="bg-success/10 text-success border-0">OK</Badge>
                          : <Badge className="bg-destructive/10 text-destructive border-0" title={row._error}>{row._error}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 50 && (
                <p className="text-xs text-muted-foreground mt-2">{t('cat_products.moreRows', { n: rows.length - 50 })}</p>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cat_common.cancel')}</Button>
          <Button onClick={onConfirm} disabled={importing || validCount === 0}>
            {importing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
            {t('cat_products.importN', { n: validCount })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
